import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography
} from '@material-ui/core';
import { Person as PersonIcon } from '@material-ui/icons';

import TaskStatusDot from '../../../common/TaskStatusDot';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import { Task, TaskEntity } from '../../tasks/Task.interface';
import { Product } from '../Product.interface';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import CartToggleButton from '../../../common/cart/CartToggleButton';

const DivCardActionArea: React.FC = (props: { children?: React.ReactNode }) => (
  <CardActionArea component='div'>{props.children}</CardActionArea>
);

const ProductBoardItem: React.FC<{
  product: Product;
  title: string;
  taskEntity: TaskEntity;
  entityId: string;
  editable?: boolean;
  loading?: boolean;
  collapsed?: boolean;
  isSelected?: boolean;
  viewDetailsLink?: string;
  onSelect?: () => void;
  onCancel?: () => void;
  onSave?: (title: string) => void;
}> = ({
  taskEntity,
  entityId,
  title: initialTitle,
  editable: initialEditable,
  loading,
  collapsed = false,
  isSelected,
  viewDetailsLink,
  onSelect,
  onCancel,
  onSave
}) => {
  const { task } = taskEntity;
  const history = useHistory();
  const [title, setTitle] = useState(initialTitle || '');
  const [editable, setEditable] = useState(initialEditable);
  const { isFieldDisabled } = useTaskEntityFields(task, false);

  const shouldCollapse: boolean = collapsed && !isSelected && !!entityId;

  const className: string = useMemo(() => {
    const classes: string[] = ['task-card'];
    isSelected && classes.push('selected');
    shouldCollapse && classes.push('collapsed');
    return classes.join(' ');
  }, [isSelected, shouldCollapse]);

  useEffect(() => {
    setTitle(initialTitle);
  }, [initialTitle]);

  const WrapperContent = editable ? React.Fragment : DivCardActionArea;

  const viewButton: JSX.Element = (
    <Box mt={2} className='view-buttons'>
      <Button
        fullWidth
        variant='outlined'
        color='primary'
        className='view-detail'
        disabled={!entityId || !viewDetailsLink}
        onClick={event => {
          event.stopPropagation();
          // allow viewing if entityId and link exist
          if (entityId && viewDetailsLink) {
            history.push(viewDetailsLink);
          }
        }}
      >
        View Details
      </Button>
      <CartToggleButton taskEntity={taskEntity} />
    </Box>
  );

  let content: JSX.Element;
  if (shouldCollapse) {
    content = (
      <WrapperContent>
        <CardContent>
          <Box className='content'>
            {task && <TaskStatusDot task={task} />}
            {entityId && (
              <Typography variant='body2' className='entity-id'>
                {entityId}
              </Typography>
            )}
            <Box tabIndex={0} className='title-text'>
              <Typography
                variant='body2'
                color={title ? 'initial' : 'textSecondary'}
              >
                {title || 'No Title'}
              </Typography>
            </Box>
          </Box>
          {viewButton}
        </CardContent>
      </WrapperContent>
    );
  } else {
    content = (
      <WrapperContent>
        <CardContent>
          {entityId && (
            <Box className='card-title'>
              <Typography variant='body1' className='entity-id'>
                {entityId}
              </Typography>
              <Box className='accessory'>
                <TaskStatusLabel size='small' task={task} />
              </Box>
            </Box>
          )}
          {editable ? (
            <TextField
              fullWidth
              autoFocus
              multiline
              variant='outlined'
              size='small'
              value={title}
              placeholder='Title'
              InputProps={{
                endAdornment: loading ? (
                  <InputAdornment position='end' className='multiline'>
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : undefined
              }}
              onClick={event => event.stopPropagation()}
              onChange={event => setTitle(event.target.value)}
              onKeyDown={event => {
                switch (event.key) {
                  case 'Escape':
                    setEditable(false);
                    setTitle(initialTitle);
                    onCancel?.();
                    break;
                  case 'Enter':
                    event.preventDefault();
                    setEditable(false);
                    onSave?.(title);
                    break;
                }
              }}
              onBlur={() => {
                setEditable(false);
                onSave?.(title);
              }}
            />
          ) : (
            <Box
              tabIndex={0}
              className='title-text'
              onClick={event => {
                if (isSelected) {
                  event.stopPropagation();
                  if (!isFieldDisabled('title')) {
                    setEditable(true);
                  }
                }
              }}
              onKeyPress={event => {
                if (isSelected) {
                  event.preventDefault();
                  event.stopPropagation();
                  if (!isFieldDisabled('title')) {
                    setEditable(true);
                  }
                }
              }}
            >
              <Typography
                variant='body2'
                color={title ? 'initial' : 'textSecondary'}
              >
                {title || 'Click to add Title'}
              </Typography>
            </Box>
          )}
          {/* <div className='title-actions'>
            <Button
              variant='outlined'
              size='small'
              color='primary'
              endIcon={
                isUpdatingProductUserNeed || isCreatingProductUserNeed ? (
                  <CircularProgress size={12} />
                ) : undefined
              }
              onClick={() => handleSave()}
            >
              {userNeed.id ? 'Update' : 'Create'}
            </Button>
          </div> */}
          {viewButton}
        </CardContent>
        {task?.user && (
          <CardContent className='info'>
            <div className='assigned'>
              <div className='icon-wrapper'>
                <PersonIcon />
              </div>
              {task.user.firstName} {task.user.lastName}
            </div>
          </CardContent>
        )}
      </WrapperContent>
    );
  }

  return (
    <Card onClick={onSelect} className={className} data-entity-id={entityId}>
      {content}
    </Card>
  );
};

export default ProductBoardItem;
